import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Alert, ActivityIndicator, Platform, Image } from 'react-native';
import { useRouter } from 'expo-router';
import { observer } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { loginWithEmail, clearDatabaseForTesting, sendPasswordRecoveryEmail, checkUserExists } from '../../services/authService';
import { userActions, globalState } from '../../state/globalState';
import { AppAuthLoginLogger } from '../../utils/logger';
import { useWebAlert } from '../../components/WebAlert';
import FormFieldError from '../../components/FormFieldError';
import WebLoadingOverlay from '../../components/WebLoadingOverlay';
import theme from '../../config/theme';
import { databaseService } from '../../services/databaseService';
import * as FileSystem from 'expo-file-system/legacy';
import { saveLastSyncTimestamp } from '../../services/syncService';
import { APP_CONSTANTS } from '@/config/constants';

// Logger específico para esta tela
const logger = AppAuthLoginLogger.Login;

const LoginScreen = observer(() => {
  const router = useRouter();
  const { showAlert } = useWebAlert();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isClearingDB, setIsClearingDB] = useState(false);
  const [isRecreating, setIsRecreating] = useState(false);
  const [isSendingRecoveryEmail, setIsSendingRecoveryEmail] = useState(false);
  
  // Controle de visibilidade da senha
  const [showPassword, setShowPassword] = useState(false);
  
  // Erros de validação para cada campo
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg);
    }
  };

  // Validar email
  const validateEmail = (text: string): boolean => {
    if (!text) {
      setEmailError('Campo obrigatório');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(text)) {
      setEmailError('Email inválido');
      return false;
    }
    setEmailError(null);
    return true;
  };
  
  // Validar senha
  const validatePassword = (text: string): boolean => {
    if (!text) {
      setPasswordError('Campo obrigatório');
      return false;
    }
    setPasswordError(null);
    return true;
  };

  // Validar campos do formulário e retornar se há erros
  const validateForm = (): boolean => {
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    return isEmailValid && isPasswordValid;
  };
  
  // Quando o usuário digita, validamos em tempo real
  const handleEmailChange = (text: string) => {
    setEmail(text);
    validateEmail(text);
    setFormError(null);
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    validatePassword(text);
    setFormError(null);
  };
  
  const handleLogin = async () => {
    logger.debug('Iniciando processo de login');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setFormError(null);
    
    try {
      const { data, error } = await loginWithEmail(email, password);
      
      if (error) {
        logger.warn('Erro no login:', error);
        let errorMessage = typeof error === 'object' && error !== null ? 
          (error as any).message || 'Não foi possível realizar o login' : 
          'Não foi possível realizar o login';

        // Mensagem específica para email não confirmado
        if ((error as any).code === 'email_not_confirmed') {
          errorMessage = 'Você precisa confirmar seu e-mail antes de fazer login. Por favor verifique seu email.';
        }

        // Mensagem específica para credenciais inválidas
        if ((error as any).code === 'invalid_credentials') {
          errorMessage = 'Email ou senha inválidos. Por favor, verifique e tente novamente.';
        }

        // Erros de autenticação são mostrados no formulário e via pop-up
        setFormError(errorMessage);
        showMessage('Erro de login', errorMessage);
        setIsLoading(false);
        return;
      }
      
      if (data?.session) {
        logger.info('Login bem-sucedido, atualizando estado...');
         // 1) atualizar o estado global
        userActions.setSession(data.session);
        // 2) inicializar (ou atualizar) o userId no banco local
        logger.info('Inicializando banco de dados para o usuário:', data.session.user.id);
        await databaseService.initialize(data.session.user.id);
        // 2.1) verificar existência do usuário após DB pronto (centralizado no fluxo pós-login)
        try {
          globalState.user.checkUserDatabaseDone.set(false);
          const userCheck = await checkUserExists(data.session.user.id);
          userActions.setUserExistsInDatabase(userCheck.exists);
        } catch (e) {
          logger.warn('Falha ao verificar existência do usuário após login:', e);
        } finally {
          globalState.user.checkUserDatabaseDone.set(true);
        }
      
        // 3) agora sim parar o loading e deixar o router navegar
        setIsLoading(false);
        // A navegação será gerenciada pelo router principal com base no estado
      }
    } catch (error) {
      logger.error('Exceção durante login:', error);
      const errorMsg = error instanceof Error ? error.message : 'Ocorreu um erro durante o login';
      setFormError(errorMsg);
      showMessage('Erro', errorMsg);
      setIsLoading(false);
    }
  };
  
  const handleClearDatabase = async () => {
    logger.debug('Iniciando limpeza do banco de dados');
    setIsClearingDB(true);
    
    try {
      const success = await clearDatabaseForTesting();
      
      if (success) {
        showMessage('Sucesso', 'Banco de dados limpo com sucesso');
      } else {
        showMessage('Erro', 'Falha ao limpar o banco de dados');
      }
    } catch (error) {
      logger.error('Erro ao limpar banco de dados:', error);
      showMessage('Erro', 'Ocorreu um erro ao limpar o banco de dados');
    } finally {
      setIsClearingDB(false);
    }
  };
  
  const confirmRecreateSchema = async (): Promise<boolean> => {
    if (Platform.OS !== 'android') {
      return new Promise((resolve) => {
        showAlert(
          "Confirmar ação",
          "Essa ação irá recriar todo o esquema de dados local. Deseja continuar?",
          [
            {
              text: "Cancelar",
              style: "cancel",
              onPress: () => resolve(false)
            },
            { 
              text: "Sim, recriar", 
              style: "destructive",
              onPress: () => resolve(true)
            }
          ]
        );
      });
    } else {
      return new Promise((resolve) => {
        Alert.alert(
          "Confirmar ação",
          "Essa ação irá recriar todo o esquema de dados local. Deseja continuar?",
          [
            {
              text: "Cancelar",
              style: "cancel",
              onPress: () => resolve(false)
            },
            { 
              text: "Sim, recriar", 
              style: "destructive",
              onPress: () => resolve(true)
            }
          ]
        );
      });
    }
  };
  
  const handleRecreateSchema = async () => {
    logger.debug('Iniciando recriação de esquema de banco de dados');
    
    const confirmed = await confirmRecreateSchema();
    if (!confirmed) {
      logger.debug('Recriação de esquema cancelada pelo usuário');
      return;
    }
    
    setIsRecreating(true);
    try {
      // Fecha o DB atual
      await databaseService.close();
      // Apaga o arquivo físico do SQLite para re-criar do zero
      const dbDir = `${FileSystem.documentDirectory}SQLite`;
      const dbFile = `${dbDir}/app_database.db`;
      logger.info('dir ', dbDir);
      logger.info('file ', dbFile);
      try {
        const info = await FileSystem.getInfoAsync(dbFile);
        if (info.exists) {
          await FileSystem.deleteAsync(dbFile, { idempotent: true });
          logger.info('Arquivo de banco apagado:', dbFile);
        }
      } catch (fsErr) {
        logger.warn('Não foi possível apagar arquivo de banco:', fsErr);
      }
      // Re-inicializa o DB com o novo esquema
      await databaseService.initialize();
      showMessage('Sucesso', 'Esquema de tabelas recriado com sucesso');
    } catch (error) {
      logger.error('Erro ao recriar esquema:', error);
      showMessage('Erro', 'Não foi possível recriar o esquema de tabelas');
    } finally {
      setIsRecreating(false);
    }
  };
  
  // Função para solicitar recuperação de senha
  const handleForgotPassword = async () => {
    // Validar email primeiro
    if (!validateEmail(email)) {
      showMessage('Atenção', 'Digite seu email para receber as instruções de recuperação de senha.');
      return;
    }
    
    setIsSendingRecoveryEmail(true);
    
    try {
      const { success, error } = await sendPasswordRecoveryEmail(email);
      
      if (error || !success) {
        logger.error('Erro ao solicitar recuperação de senha:', error);
        showMessage('Erro', 'Não foi possível enviar o email de recuperação. Verifique se o email está correto.');
      } else {
        logger.info('Email de recuperação enviado com sucesso');
        showMessage('Email enviado', 'Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.');
      }
    } catch (error) {
      logger.error('Exceção ao solicitar recuperação de senha:', error);
      showMessage('Erro', 'Ocorreu um erro inesperado. Tente novamente mais tarde.');
    } finally {
      setIsSendingRecoveryEmail(false);
    }
  };

  const shouldShowDebugActions = APP_CONSTANTS.FEATURE_FLAGS?.SHOW_DEBUG_ACTIONS !== false;
  
  return (
    <View style={styles.container}>
      {/* Overlay de carregamento para web */}
      <WebLoadingOverlay 
        visible={isLoading && Platform.OS === 'web'} 
        message="Autenticando... Por favor, aguarde."
      />
      <View style={styles.contentWrapper}>
        <View style={styles.headerContainer}>
          <Image
            source={require('../../assets/icon.png')}
            style={styles.brandIcon}
            resizeMode="contain"
          />
          <Text style={styles.title}>App Gincho Checklists</Text>
          <Text style={styles.subtitle}>Bem-vindo de volta</Text>
        </View>

        <View style={styles.form}>
          <Text style={styles.label}>Email</Text>
          <View style={styles.inputContainer}>
            <Ionicons 
              name="mail-outline" 
              size={20} 
              color={emailError ? theme.colors.util.error : theme.colors.text.secondary} 
              style={styles.inputIcon}
            />
            <TextInput
              style={[
                styles.input, 
                emailError ? styles.inputError : null
              ]}
              value={email}
              onChangeText={handleEmailChange}
              placeholder="Seu email"
              placeholderTextColor={theme.colors.text.tertiary}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
            />
          </View>
          <FormFieldError message={emailError} />
          
          <Text style={styles.label}>Senha</Text>
          <View style={styles.inputContainer}>
            <Ionicons 
              name="lock-closed-outline" 
              size={20} 
              color={passwordError ? theme.colors.util.error : theme.colors.text.secondary} 
              style={styles.inputIcon}
            />
            <TextInput
              style={[
                styles.input, 
                passwordError ? styles.inputError : null,
                { flex: 1 }
              ]}
              value={password}
              onChangeText={handlePasswordChange}
              placeholder="Sua senha"
              placeholderTextColor={theme.colors.text.tertiary}
              secureTextEntry={!showPassword}
            />
            <TouchableOpacity 
              style={styles.eyeIcon}
              onPress={() => setShowPassword(!showPassword)}
            >
              <Ionicons 
                name={showPassword ? "eye-off-outline" : "eye-outline"} 
                size={22} 
                color={theme.colors.text.secondary} 
              />
            </TouchableOpacity>
          </View>
          <FormFieldError message={passwordError} />

          {formError && (
            <View style={styles.formErrorContainer}>
              <Ionicons name="alert-circle" size={18} color={theme.colors.util.error} />
              <Text style={styles.formErrorText}>{formError}</Text>
            </View>
          )}
          
          <TouchableOpacity 
            style={styles.button} 
            onPress={handleLogin} 
            disabled={isLoading}
          >
            {isLoading && Platform.OS !== 'web' ? (
              <ActivityIndicator color={theme.colors.text.inverse} />
            ) : (
              <View style={styles.buttonContent}>
                <Ionicons name="log-in-outline" size={18} color={theme.colors.text.inverse} />
                <Text style={styles.buttonText}>Entrar</Text>
              </View>
            )}
          </TouchableOpacity>
        
        {/* Botão de esqueci a senha */}
        <TouchableOpacity 
          onPress={handleForgotPassword} 
          style={styles.forgotPasswordContainer}
          disabled={isSendingRecoveryEmail}
        >
          {isSendingRecoveryEmail ? (
            <ActivityIndicator size="small" color={theme.colors.primary.default} />
          ) : (
            <>
              <Ionicons name="help-circle-outline" size={16} color={theme.colors.primary.default} />
              <Text style={styles.forgotPasswordText}>
                Esqueceu sua senha? <Text style={styles.forgotPasswordLink}>Recuperar acesso</Text>
              </Text>
            </>
          )}
        </TouchableOpacity>
        
        <TouchableOpacity 
          onPress={() => router.push('/signup')} 
          style={styles.signupContainer}
        >
          <Ionicons name="person-add-outline" size={16} color={theme.colors.primary.default} />
          <Text style={styles.signupText}>
            Não tem uma conta? <Text style={styles.signupLink}>Criar conta</Text>
          </Text>
        </TouchableOpacity>
        
        {shouldShowDebugActions && (
          <>
            {/* Overlay de carregamento para operações de teste em web */}
            <WebLoadingOverlay 
              visible={(isClearingDB || isRecreating) && Platform.OS === 'web'}
              message={isClearingDB ? "Limpando banco de dados..." : "Recriando estrutura do banco..."}
            />
            
            {/* Botão para limpar o banco de dados (apenas para testes) */}
            <TouchableOpacity 
              style={[styles.button, styles.clearButton]} 
              onPress={handleClearDatabase} 
              disabled={isClearingDB}
            >
              {isClearingDB && Platform.OS !== 'web' ? (
                <ActivityIndicator color={theme.colors.text.inverse} />
              ) : (
                <View style={styles.buttonContent}>
                  <Ionicons name="trash-outline" size={18} color={theme.colors.text.inverse} />
                  <Text style={styles.buttonText}>Limpar Banco de Dados</Text>
                </View>
              )}
            </TouchableOpacity>
            
            {/* Botão para recriar esquema de tabelas */}
            <TouchableOpacity 
              style={[styles.button, styles.recreateButton]} 
              onPress={handleRecreateSchema} 
              disabled={isRecreating}
            >
              {isRecreating && Platform.OS !== 'web' ? (
                <ActivityIndicator color={theme.colors.text.inverse} />
              ) : (
                <View style={styles.buttonContent}>
                  <Ionicons name="refresh-outline" size={18} color={theme.colors.text.inverse} />
                  <Text style={styles.buttonText}>Recriar Esquema de Tabelas</Text>
                </View>
              )}
            </TouchableOpacity>
            {/* Botão para salvar timestamp fixo 01/01/2025 10:00:00 */}
            <TouchableOpacity
              style={[styles.button, { backgroundColor: '#0077cc', marginTop: 16 }]}
              onPress={async () => {
                // 01/01/2025 10:00:00 GMT-3 (Brasília) = 2025-01-01T13:00:00.000Z UTC
                const fixedDate = new Date('2025-01-01T13:00:00.000Z').getTime();
                saveLastSyncTimestamp(fixedDate);
                showMessage('Timestamp salvo', 'Timestamp de sincronização salvo como 01/01/2025 10:00:00.');
              }}
            >
              <View style={styles.buttonContent}>
                <Ionicons name="calendar-outline" size={18} color={theme.colors.text.inverse} />
                <Text style={styles.buttonText}>Salvar Timestamp 01/01/2025 10:00</Text>
              </View>
            </TouchableOpacity>
          </>
        )}
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 420 : undefined,
  },
  headerContainer: {
    marginBottom: 32,
    alignItems: 'center',
  },
  brandIcon: {
    width: 96,
    height: 96,
    borderRadius: 24,
    backgroundColor: theme.colors.background.secondary,
    padding: 16,
    marginBottom: 16,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 18,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  form: {
    width: '100%',
  },
  label: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  inputIcon: {
    marginLeft: 12,
  },
  eyeIcon: {
    padding: 12,
  },
  input: {
    padding: 16,
    fontSize: 16,
    color: theme.colors.text.primary,
    flex: 1,
  },
  inputError: {
    borderColor: theme.colors.util.error,
  },
  formErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: theme.spacing.sm,
    justifyContent: 'center',
  },
  formErrorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.md,
    marginLeft: theme.spacing.xs,
  },
  button: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginTop: 16,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: theme.colors.text.inverse,
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  clearButton: {
    backgroundColor: '#ff3b30',
    marginTop: 16,
  },
  recreateButton: {
    backgroundColor: '#ffa500',
    marginTop: 16,
  },
  signupContainer: {
    marginTop: 20,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  signupText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginLeft: 8,
  },
  signupLink: {
    color: theme.colors.primary.default,
    fontWeight: 'bold',
  },
  forgotPasswordContainer: {
    marginTop: 16,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  forgotPasswordText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginLeft: 8,
  },
  forgotPasswordLink: {
    color: theme.colors.primary.default,
    fontWeight: 'bold',
  },
});

export default LoginScreen; 
